import { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, CircularProgress, Box, Typography, Chip, Button } from '@mui/material';
import {
  ArrowBack as BackIcon,
  Share as ShareIcon,
  Description as ReportIcon,
  Tune as ConfigureIcon
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { PageLayout } from '../../PageLayout';
import { EngagementEditorProvider, SideBySideView } from '../../Compounds/Advise/SideBySideView';
import {
  AdviseEngagement,
  EngagementStatus,
  AdviseSupplier,
  AdviseCustomer,
  EngagementConfiguration
} from '../../../types/advise';
import { engagementStatusLabel } from '../../../constants/advise';
import {
  getAdviseActiveEngagementSubject,
  getAdviseEngagementLoadingSubject,
  useAdviseEngagements
} from '../../../store/AdviseEngagement';
import { getAdviseSupplierSubject, useAdviseSuppliers } from '../../../store/AdviseSupplier';
import { canEditEngagement } from './canEditEngagement';
import { usePendingSaves } from './usePendingSaves';
import { savesForPatch } from './engagementSaves';
import { useEngagementDiscovery } from './useEngagementDiscovery';
import { useEngagementFunctionalityMatrix } from './useEngagementFunctionalityMatrix';
import { discoveryProgress } from '../../Compounds/Advise/SideBySideView/DiscoveryPanel.calc';
import { fetchAdviseCustomerById } from '../../../Api/Advise/Customers';
import { addError } from '../../../store/Error';
import { useSnackbar } from '../../../Context/SnackbarContext';
import { ShareEngagementDialog } from '../../Compounds/Advise/ShareEngagementDialog';
import { ReportGenerator } from '../../Compounds/Advise/Reports';
import {
  ConfigurationExit,
  EngagementConfigurationScreen,
  StepsLoadError
} from '../../Compounds/Advise/EngagementSteps';
import { useEngagementConfiguration } from '../../../store/AdviseEngagementConfiguration';

export const AdviseAnalysisPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const engagementId = searchParams.get('id');

  const [engagement, setEngagement] = useState<AdviseEngagement | null>(null);
  const [suppliers, setSuppliers] = useState<AdviseSupplier[]>([]);
  const [customer, setCustomer] = useState<AdviseCustomer | null>(null);
  const [loading, setLoading] = useState(true);
  const [showShare, setShowShare] = useState(false);
  const [showReport, setShowReport] = useState(false);

  const { fetchEngagementById } = useAdviseEngagements();
  const { fetchSuppliers } = useAdviseSuppliers();
  const { setSnack } = useSnackbar();

  const canEdit = canEditEngagement(engagement, engagementId);
  const {
    configuration,
    setConfiguration,
    failed: configurationFailed,
    retry
  } = useEngagementConfiguration(engagementId, { refresh: true });
  const canConfigure = canEdit && !!configuration?.can_configure;
  const configuring = canConfigure && searchParams.get('configure') === '1';
  const [quitting, setQuitting] = useState(false);

  // Subscribe to store updates
  useEffect(() => {
    const engagementSub = getAdviseActiveEngagementSubject().subscribe((data) => setEngagement(data));
    const loadSub = getAdviseEngagementLoadingSubject().subscribe((val) => setLoading(val));
    const supplierSub = getAdviseSupplierSubject().subscribe((data) => setSuppliers(data));

    return () => {
      engagementSub.unsubscribe();
      loadSub.unsubscribe();
      supplierSub.unsubscribe();
    };
  }, []);

  // Fetch data on mount
  useEffect(() => {
    if (engagementId) {
      fetchEngagementById(engagementId);
    }
    fetchSuppliers();
  }, [engagementId]);

  // The engagement can belong to any account, so its customer is loaded by id rather than from the account picker.
  useEffect(() => {
    setCustomer(null);
    const customerId = engagement?.customer_id;
    if (!customerId) return;
    let current = true;
    const loadCustomer = async () => {
      const { data, error } = await fetchAdviseCustomerById(customerId);
      if (!current) return;
      addError('advise-customer', error?.message);
      setCustomer(data);
    };
    loadCustomer();
    return () => {
      current = false;
    };
  }, [engagement?.customer_id]);

  // Surfaces failures so optimistic UI can't mask a rejected save.
  const handleSaveError = useCallback(
    (message: string) => {
      addError('advise-engagement', message);
      setSnack({ message: 'Failed to save changes. Please try again.', open: true, type: 'error' });
    },
    [setSnack]
  );
  const pendingSaves = usePendingSaves(canEdit, handleSaveError);
  const { debouncedSave, flushSaves } = pendingSaves;

  // Every way out to the dashboard leaves only once every edit is saved; a failed save keeps the user here.
  const leaveToDashboard = useCallback(async () => {
    setQuitting(true);
    if (await flushSaves()) navigate('/advise');
    else setQuitting(false);
  }, [flushSaves, navigate]);

  // The report reads the matrix from the API, so it opens only once every edit has saved.
  const openReport = useCallback(async () => {
    if (await flushSaves()) setShowReport(true);
  }, [flushSaves]);

  const setConfiguring = useCallback(
    (on: boolean) =>
      setSearchParams(
        (params) => {
          const next = new URLSearchParams(params);
          if (on) next.set('configure', '1');
          else next.delete('configure');
          return next;
        },
        { replace: true }
      ),
    [setSearchParams]
  );

  const handleConfigurationSaved = useCallback(
    (saved: EngagementConfiguration, exit: ConfigurationExit) => {
      setConfiguration(saved);
      if (exit === 'quit') leaveToDashboard();
      else setConfiguring(false);
    },
    [leaveToDashboard, setConfiguration, setConfiguring]
  );

  const engagementRef = useRef(engagement);
  useEffect(() => {
    engagementRef.current = engagement;
  }, [engagement]);

  // Every panel writes through this, so a read-only engagement sends nothing even where a panel leaves a control live.
  const editEngagement = useCallback(
    (patch: Partial<AdviseEngagement>) => {
      const current = engagementRef.current;
      if (!canEdit || !current) return;
      // Kept in the ref at once, so a second patch before the next render builds on this one.
      const next = { ...current, ...patch };
      engagementRef.current = next;
      setEngagement(next);
      savesForPatch(patch).forEach(({ key, save }) => debouncedSave(key, () => save(next)));
    },
    [canEdit, debouncedSave]
  );

  const discovery = useEngagementDiscovery(engagementId, canEdit, debouncedSave);
  const discoveryProgressByStep = useMemo(() => {
    if (!discovery.sections) return undefined;
    const { required, requiredAnswered } = discoveryProgress(discovery.sections);
    return { discovery: { answered: requiredAnswered, required } };
  }, [discovery.sections]);

  const functionalityMatrix = useEngagementFunctionalityMatrix(engagementId, canEdit, pendingSaves);

  const editor = useMemo(
    () => (engagement ? { engagement, suppliers, canEdit, editEngagement, discovery, functionalityMatrix } : null),
    [engagement, suppliers, canEdit, editEngagement, discovery, functionalityMatrix]
  );

  const selectedSuppliers = useMemo(
    () => suppliers.filter((v) => engagement?.selected_suppliers?.includes(v.id)),
    [suppliers, engagement?.selected_suppliers]
  );

  // ---- Render ----

  if (!engagementId) {
    return (
      <PageLayout
        pageTitle="Advise Analysis"
        dataCy="advise-analysis-page">
        <Alert severity="warning">
          No engagement ID provided. Go back to{' '}
          <a
            href="/advise/engagements"
            className="underline">
            engagements
          </a>{' '}
          and select one.
        </Alert>
      </PageLayout>
    );
  }

  if (loading && !engagement) {
    return (
      <PageLayout
        pageTitle="Advise Analysis"
        dataCy="advise-analysis-page">
        <Box className="flex items-center justify-center p-12">
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  if (!engagement) {
    return (
      <PageLayout
        pageTitle="Advise Analysis"
        dataCy="advise-analysis-page">
        <Alert severity="error">Engagement not found.</Alert>
      </PageLayout>
    );
  }

  const pageActions = (
    <Box className="flex items-center gap-2">
      <Chip
        label={engagementStatusLabel(engagement.status)}
        size="small"
        color={engagement.status === EngagementStatus.PUBLISHED ? 'success' : 'default'}
        variant="outlined"
      />
      {canConfigure && !configuring && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<ConfigureIcon />}
          onClick={() => setConfiguring(true)}>
          Configure
        </Button>
      )}
      {canEdit && (
        <Button
          size="small"
          variant="outlined"
          startIcon={<ShareIcon />}
          onClick={() => setShowShare(true)}>
          Share
        </Button>
      )}
      <Button
        size="small"
        variant="outlined"
        startIcon={<ReportIcon />}
        onClick={openReport}>
        Report
      </Button>
      <Button
        size="small"
        startIcon={<BackIcon />}
        onClick={() => (location.key === 'default' ? navigate('/advise') : navigate(-1))}>
        Back
      </Button>
    </Box>
  );

  return (
    <PageLayout
      pageTitle={engagement.engagement_name || 'Advise Analysis'}
      pageActions={pageActions}
      dataCy="advise-analysis-page">
      <Box className="flex items-center gap-2 mb-4">
        {customer && (
          <Typography
            variant="body2"
            color="textSecondary">
            Customer: {customer.company_name}
          </Typography>
        )}
        <Typography
          variant="body2"
          color="textSecondary">
          {selectedSuppliers.length} supplier{selectedSuppliers.length !== 1 ? 's' : ''}
        </Typography>
      </Box>

      {configurationFailed && (
        <Box className="mb-4">
          <StepsLoadError
            consequence="every tab shows and Configure is unavailable"
            onRetry={retry}
          />
        </Box>
      )}

      {configuring && configuration ? (
        <EngagementConfigurationScreen
          engagementId={engagement.id}
          steps={configuration.steps}
          onSaved={handleConfigurationSaved}
          onExit={leaveToDashboard}
          exiting={quitting}
        />
      ) : (
        <EngagementEditorProvider value={editor}>
          <SideBySideView
            steps={configuration?.steps}
            stepFlow={
              canEdit
                ? {
                    onBack: canConfigure ? () => setConfiguring(true) : undefined,
                    onSaveAndQuit: leaveToDashboard,
                    saving: quitting,
                    requiredProgress: discoveryProgressByStep
                  }
                : undefined
            }
          />
        </EngagementEditorProvider>
      )}

      {showShare && (
        <ShareEngagementDialog
          open={showShare}
          onClose={() => setShowShare(false)}
          engagement={engagement}
          onShared={(updated) => setEngagement(updated)}
        />
      )}

      {showReport && (
        <ReportGenerator
          engagement={engagement}
          suppliers={suppliers}
          customer={customer}
          open={showReport}
          onClose={() => setShowReport(false)}
        />
      )}
    </PageLayout>
  );
};
